'use client'

/**
 * Purpose: Live MediaStream lifecycle for camera composers.
 * Inputs: facingMode, ideal dimensions, optional onError callback.
 * Outputs: videoRef, start/stop, isActive.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { cameraFailureReason, type CameraFailureReason } from '@lib/camera-errors'

export type UseCameraStreamOptions = {
  facingMode: 'user' | 'environment'
  idealWidth?: number
  idealHeight?: number
  onError?: (reason: CameraFailureReason, err: unknown) => void
}

/** True when play() was aborted because srcObject changed or a newer start() won. */
function isPlayInterrupted(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  if (err.name === 'AbortError') return true
  return /interrupted by a new load request/i.test(err.message)
}

// Manages getUserMedia lifecycle for camera composers.
export function useCameraStream({
  facingMode,
  idealWidth = 1280,
  idealHeight = 960,
  onError,
}: UseCameraStreamOptions) {
  // videoRef attaches to the preview element; streamRef holds active MediaStream tracks.
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [isActive, setIsActive] = useState(false)
  // Keep onError off the start() dependency list so parent re-renders do not restart the stream.
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  // Monotonic generation — a newer start() supersedes in-flight getUserMedia / play().
  const startGenerationRef = useRef(0)

  // Stop all tracks — called before restart and on unmount.
  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null
    setIsActive(false)
  }, [])

  const start = useCallback(async () => {
    const generation = ++startGenerationRef.current

    // Tear down any prior stream without going through stop()'s setState until we know this gen wins.
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: idealWidth },
          height: { ideal: idealHeight },
        },
      })

      // A newer start() won while we awaited the permission prompt / device.
      if (generation !== startGenerationRef.current) {
        stream.getTracks().forEach((tr) => tr.stop())
        return null
      }

      streamRef.current = stream
      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        try {
          // Autoplay may reject until the element is in the layout — surface via onError.
          await video.play()
        } catch (playErr) {
          // Superseded mid-play — the newer start owns the element now.
          if (generation !== startGenerationRef.current) {
            stream.getTracks().forEach((tr) => tr.stop())
            return null
          }
          // Interrupted load but the element is already playing (autoPlay / racing play) — treat as success.
          if (isPlayInterrupted(playErr) && !video.paused) {
            setIsActive(true)
            return stream
          }
          throw playErr
        }
      }

      if (generation !== startGenerationRef.current) {
        stream.getTracks().forEach((tr) => tr.stop())
        streamRef.current = null
        return null
      }

      setIsActive(true)
      return stream
    } catch (err) {
      // Superseded failures must not fire onError — that was the false camera_unavailable noise.
      if (generation !== startGenerationRef.current) {
        return null
      }
      onErrorRef.current?.(cameraFailureReason(err), err)
      throw err
    }
  }, [facingMode, idealHeight, idealWidth])

  // Release camera when the composing component unmounts.
  useEffect(() => () => stop(), [stop])

  return { videoRef, start, stop, isActive, streamRef }
}
